import { zodResolver } from '@hookform/resolvers/zod'
import type { HttpError } from '@refinedev/core'
import { useForm } from '@refinedev/react-hook-form'
import type { Location } from '@sirius/contracts/admin'
import { locationCreateInputSchema } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import type { z } from 'zod'
import { Button } from '~/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import {
  FORM_RECORD_QUERY_OPTIONS,
  useRecordPending,
} from '~/lib/formRecordQuery'
import { NotFoundPage } from '~/pages/notFound'

/** The tenant comes from the host (data provider), never from the form. */
const locationFormSchema = locationCreateInputSchema.omit({
  tenantId: true,
})
type LocationFormValues = z.input<typeof locationFormSchema>

export function LocationForm({
  action,
  id,
}: {
  action: 'create' | 'edit'
  id?: string
}) {
  const { t } = useTranslation()
  const form = useForm<Location, HttpError, LocationFormValues>({
    resolver: zodResolver(locationFormSchema),
    defaultValues: { name: '' },
    refineCoreProps: {
      resource: 'locations',
      action,
      id,
      redirect: 'show',
      queryOptions: FORM_RECORD_QUERY_OPTIONS,
    },
  })
  const { onFinish, formLoading, query } = form.refineCore
  // in edit mode nothing may be typed or saved before the record has settled
  const pending = useRecordPending(action, query)

  if (query?.error?.statusCode === 404) {
    return <NotFoundPage />
  }
  if (query?.error) {
    return (
      <p
        role="alert"
        className="text-destructive text-sm"
      >
        {t('locationsPage.form.loadError', { message: query.error.message })}
      </p>
    )
  }

  return (
    <Form {...form}>
      <form
        aria-busy={pending}
        onSubmit={form.handleSubmit(onFinish)}
        className="max-w-md space-y-4"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('locationsPage.form.name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={pending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="submit"
          disabled={formLoading || pending}
        >
          {t('locationsPage.form.save')}
        </Button>
      </form>
    </Form>
  )
}
